import { useContext, useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { withTheme } from 'styled-components';

import { AppContext } from '../../../context/AppContext';
import Accordion from '../../Accordion';
import Expand from '../../Icon/Expand';
import StyledTitle from '../../Styled/StyledTitle';

import {
  ActivityDescriptionsContent,
  DisabledChangesContent,
  HistoricChangesContent,
} from './BatterySocAccordionContent';
import { ChartTitleRow, ChartWrapper, Container, ExpandButton, StyledHighlightInfo } from './FurtherInfo.styles';
import SocChart from './SocChart';
import { buildNowToFutureLink } from './socChartDerivations';
import SocChartLegend from './SocChartLegend';
import SocChartModal from './SocChartModal';
import SocChartToggles from './SocChartToggles';
import { useHistoricChartSegments } from './useHistoricChartSegments';
import { useSolarLine } from './useSolarLine';
import { useTariffShadingBlocks } from './useTariffShadingBlocks';
import { useTooltipBlacklist } from './useTooltipBlacklist';

const NoDataMessage = () => (
  <Alert variant="warning" style={{ marginBottom: 0 }}>
    Not enough data yet. Check back in 30 minutes.
  </Alert>
);

const FurtherInfo = ({
  intersectionX,
  intersectionY,
  historicChartData,
  behaviorChartData,
  futureRecords,
  futureChartSegments,
  costWindows,
  costWindowsExport,
  batteryCapacityKwh,
  loopIsDisabled,
  nowOverrideSecs = /** @type {number | undefined} */ (undefined),
  initialTariffRateView = 'import',
  theme,
}) => {
  const { userDetails, showSimulationMode } = useContext(AppContext);
  const [showBatteryActions, setShowBatteryActions] = useState(!loopIsDisabled);
  const [showSolarGeneration, setShowSolarGeneration] = useState(true);
  const [showTariffRates, setShowTariffRates] = useState(!loopIsDisabled);
  const [tariffRateView, setTariffRateView] = useState(initialTariffRateView);
  const [chartModalOpen, setChartModalOpen] = useState(false);

  const chartAvailable =
    !!historicChartData?.length && !!behaviorChartData?.length && (!!futureRecords?.length || loopIsDisabled);

  const {
    active: safeActiveTariffShadingBlocks,
    importBlocks,
    exportBlocks,
  } = useTariffShadingBlocks({
    behaviorChartData,
    costWindows,
    costWindowsExport,
    tariffRateView,
  });

  const importHasNoShading = importBlocks.cheap.length === 0 && importBlocks.peak.length === 0;
  const exportHasNoShading = exportBlocks.cheap.length === 0 && exportBlocks.peak.length === 0;

  const importExportRatesDiffer = useMemo(() => {
    if (!costWindows?.length || !costWindowsExport?.length) return false;
    const importKey = costWindows.map((w) => `${w.rate}:${w.start_timestamp_utc}`).join('|');
    const exportKey = costWindowsExport.map((w) => `${w.rate}:${w.start_timestamp_utc}`).join('|');
    return importKey !== exportKey;
  }, [costWindows, costWindowsExport]);

  // When rates differ, auto-select whichever direction has shading so the active
  // view is never stuck on a direction with a disabled/greyed-out segment button.
  useEffect(() => {
    if (!importExportRatesDiffer) return;
    if (importHasNoShading && !exportHasNoShading) setTariffRateView('export');
    else if (exportHasNoShading && !importHasNoShading) setTariffRateView('import');
  }, [importExportRatesDiffer, importHasNoShading, exportHasNoShading]);

  const accordionData = [
    {
      title: 'Show list of changes to the battery so far today',
      content: <HistoricChangesContent historicChartData={historicChartData} />,
    },
    {
      title: 'Battery actions explained',
      content: <ActivityDescriptionsContent />,
    },
  ];

  const disabledData = [
    {
      title: 'Show list of changes to the battery so far today',
      content: <DisabledChangesContent appName={theme.language.appName} />,
    },
  ];

  const placeholderXAxis = Array.from(
    { length: 48 },
    (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}`
  );

  const chartTitle = loopIsDisabled
    ? 'Battery history'
    : showSimulationMode === false && !!userDetails?.simulation_mode
      ? 'Battery state of charge today so far'
      : 'Your 12-hour personalised Optimisation plan';

  const tooltipBlacklist = useTooltipBlacklist(loopIsDisabled);

  const nowToFutureLink = useMemo(() => {
    return buildNowToFutureLink({ intersectionX, intersectionY, futureChartSegments });
  }, [futureChartSegments, intersectionX, intersectionY]);

  const historicChartSegments = useHistoricChartSegments(historicChartData, chartAvailable);

  const { solarLineData, solarMaxKwh, solarLineDataLeft } = useSolarLine(
    behaviorChartData,
    chartAvailable,
    showSolarGeneration,
    intersectionX
  );

  return (
    <Container>
      <ChartTitleRow>
        <StyledTitle style={{ marginBottom: 0 }}>{chartTitle}</StyledTitle>
        {chartAvailable && (
          <ExpandButton onClick={() => setChartModalOpen(true)} aria-label="Expand chart">
            <Expand colour={theme.colors.grey} width={22} height={22} />
          </ExpandButton>
        )}
      </ChartTitleRow>

      {chartAvailable && !loopIsDisabled && (
        <SocChartToggles
          showBatteryActions={showBatteryActions}
          onToggleBatteryActions={() => setShowBatteryActions((prev) => !prev)}
          showSolarGeneration={showSolarGeneration}
          onToggleSolarGeneration={() => setShowSolarGeneration((prev) => !prev)}
          showTariffRates={showTariffRates}
          onToggleTariffRates={() => setShowTariffRates((prev) => !prev)}
          importExportRatesDiffer={importExportRatesDiffer}
          tariffRateView={tariffRateView}
          onTariffRateViewChange={setTariffRateView}
          importHasNoShading={importHasNoShading}
          exportHasNoShading={exportHasNoShading}
        />
      )}

      {chartAvailable ? (
        <ChartWrapper>
          <SocChart
            chartAvailable={chartAvailable}
            loopIsDisabled={loopIsDisabled}
            showBatteryActions={showBatteryActions}
            showSolarGeneration={showSolarGeneration}
            showTariffRates={showTariffRates}
            tariffRateView={tariffRateView}
            behaviorChartData={behaviorChartData}
            historicChartData={historicChartData}
            historicChartSegments={historicChartSegments}
            futureChartSegments={futureChartSegments}
            nowToFutureLink={nowToFutureLink}
            safeActiveTariffShadingBlocks={safeActiveTariffShadingBlocks}
            solarLineData={solarLineData}
            solarLineDataLeft={solarLineDataLeft}
            solarMaxKwh={solarMaxKwh}
            tooltipBlacklist={tooltipBlacklist}
            intersectionX={intersectionX}
            intersectionY={intersectionY}
            batteryCapacityKwh={batteryCapacityKwh}
            placeholderXAxis={placeholderXAxis}
            nowOverrideSecs={nowOverrideSecs}
          />
        </ChartWrapper>
      ) : (
        <NoDataMessage />
      )}

      {chartAvailable && (
        <SocChartLegend
          showBatteryActions={showBatteryActions}
          showTariffRates={showTariffRates}
          showSolarGeneration={showSolarGeneration}
          tariffRateView={tariffRateView}
        />
      )}

      {!loopIsDisabled && <Accordion data={[accordionData[1]]} />}

      <p style={{ margin: 0 }}>
        {theme.language.appName}s your electricity bill by charging your battery when electricity is cheap, and using
        this energy when electricity is expensive.
      </p>

      <StyledHighlightInfo>
        <p>
          {theme.language.brandName}'s optimisation runs every half hour and reacts to changes in your energy demand or
          generation.
        </p>
      </StyledHighlightInfo>

      {chartAvailable && <Accordion data={!loopIsDisabled ? [accordionData[0]] : disabledData} />}

      <SocChartModal
        show={chartModalOpen}
        onHide={() => setChartModalOpen(false)}
        chartTitle={chartTitle}
        chartAvailable={chartAvailable}
        loopIsDisabled={loopIsDisabled}
        importExportRatesDiffer={importExportRatesDiffer}
        showBatteryActions={showBatteryActions}
        onToggleBatteryActions={() => setShowBatteryActions((prev) => !prev)}
        showSolarGeneration={showSolarGeneration}
        onToggleSolarGeneration={() => setShowSolarGeneration((prev) => !prev)}
        showTariffRates={showTariffRates}
        onToggleTariffRates={() => setShowTariffRates((prev) => !prev)}
        tariffRateView={tariffRateView}
        onTariffRateViewChange={setTariffRateView}
        behaviorChartData={behaviorChartData}
        historicChartData={historicChartData}
        historicChartSegments={historicChartSegments}
        futureChartSegments={futureChartSegments}
        nowToFutureLink={nowToFutureLink}
        safeActiveTariffShadingBlocks={safeActiveTariffShadingBlocks}
        solarLineData={solarLineData}
        solarLineDataLeft={solarLineDataLeft}
        solarMaxKwh={solarMaxKwh}
        tooltipBlacklist={tooltipBlacklist}
        intersectionX={intersectionX}
        intersectionY={intersectionY}
        batteryCapacityKwh={batteryCapacityKwh}
        placeholderXAxis={placeholderXAxis}
        importHasNoShading={importHasNoShading}
        exportHasNoShading={exportHasNoShading}
        nowOverrideSecs={nowOverrideSecs}
      />
    </Container>
  );
};

export default withTheme(FurtherInfo);
